/** biome-ignore-all lint/suspicious/noArrayIndexKey: <explanation> */
import React from 'react';
import classNames from 'classnames';
import { DragSource, DropTarget } from 'react-dnd';
import flow from 'lodash.flow';
import { Observe } from 'rewire-core';
import { ICell, IRow, ReorderableGridRowsCell, ReorderableGridRowsCellProps } from 'rewire-grid';
import { withStyles } from 'rewire-ui';
import Divider from '@material-ui/core/Divider';
import { Theme } from '@material-ui/core/styles';
import { ClipboardText as PostedIcon, ClipboardOutline as UnpostedIcon } from 'mdi-material-ui';
import { confirmationModel } from '../ui/ConfirmationDialog';
import MessageIcon from '../ui/MessageIcon';
import scheduleModel, { ITimeEntry, IShiftEntry } from './ScheduleModel';

export const ScheduleGridReorderableCellRenderer = (props: ReorderableGridRowsCellProps) => {
  const onMoveRow = (row: IRow, byRows: number) => {
    scheduleModel.moveJobPosition(row.id, byRows, true);
  };

  const onSwapRow = (sourceRow: IRow, targetRow: IRow) => {
    scheduleModel.swapJobPositions(sourceRow.id, targetRow.id, true);
  };

  const canMoveRow = (_row: IRow) => {
    return true;
  };

  const canDropRow = (_sourceRow: IRow, _targetRow: IRow) => {
    return true;
  };

  return (
    <ReorderableGridRowsCell
      {...props}
      onMoveRow={onMoveRow}
      onSwapRow={onSwapRow}
      canMoveRow={canMoveRow}
      canDropRow={canDropRow}
    />
  );
};

const jobCellStyles = (theme: Theme) => ({
  root: {
    width: '100%',
    height: '100%',
    // fontSize: `${scheduleModel.viewMode === 'Job' ? '1.1em' : '1em'}`,
    fontSize: '1.1em',
  },
  rootWithValue: {
    lineHeight: '1.5em',
  },
  unsavedNewJob: {
    lineHeight: '1.4em',
    fontWeight: 500,
    color: theme.palette.secondary.main,
  },
  occupation: {
    fontWeight: 700,
    color: theme.palette.dialogPrimary.main,
  },
  generator: {
    fontSize: '0.9em',
  },
  effective: {
    display: 'inline-block',
    fontSize: '0.85em',
    fontStyle: 'italic',
  },
  dateDivider: {
    padding: '0px 2px',
  },
});

interface ICellProps {
  cell: ICell;
  classes: Record<string, string>;
}

export const ScheduleJobCell: React.SFC<ICellProps> = React.memo(
  withStyles(jobCellStyles, (props) => {
    const { cell, classes } = props;
    if (!cell.value) {
      return (
        <div className={classes.root} style={{ textAlign: cell.align }}>
          <div className={classes.unsavedNewJob}>Unsaved New Job</div>
        </div>
      );
    }
    return (
      <Observe
        render={() => {
          const occupation = cell.value.text.occupation;
          const generator = cell.value.text.generator;
          const effectiveStart = cell.value.text.effectiveStart;
          const effectiveEnd = cell.value.text.effectiveEnd;
          return (
            <div className={classNames(classes.root, classes.rootWithValue)} style={{ textAlign: cell.align }}>
              <div className={classes.occupation}>{occupation}</div>
              {generator && <div className={classes.generator}>{generator}</div>}
              <div className={classes.effective}>
                {effectiveStart && effectiveEnd && (
                  <>
                    <span>{effectiveStart}</span> <span className={classes.dateDivider}>to</span> <span>{effectiveEnd}</span>
                  </>
                )}
              </div>
            </div>
          );
        }}
      />
    );
  }),
  () => false,
);

const employeeCellStyles = (_theme: Theme) => ({
  root: {
    width: '100%',
    height: '100%',
    lineHeight: '1.5em',
    textDecoration: 'none',
    fontWeight: 500,
    // fontSize: `${scheduleModel.viewMode === 'Job' ? '1em' : '1.1em'}`,
    fontSize: '1.1em',
  },
});

export const ScheduleEmployeeCell: React.SFC<ICellProps> = React.memo(
  withStyles(employeeCellStyles, (props) => {
    const { cell, classes } = props;
    return (
      <div className={classes.root} style={{ textAlign: cell.align }}>
        <div>{cell.value?.name ?? ''}</div>
      </div>
    );
  }),
  () => false,
);

const shiftColumnHeaderStyles = (theme: Theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  postedStatusIcon: {
    marginLeft: '12px',
    width: '0.9em',
    height: '0.9em',
  },
  postedIcon: {
    color: theme.palette.secondary.main,
  },
  unpostedIcon: {
    color: theme.palette.neutralLight.main,
  },
});

export const ScheduleShiftColumnHeader: React.SFC<ICellProps> = React.memo(
  withStyles(shiftColumnHeaderStyles, (props) => {
    const { cell, classes } = props;

    return (
      <Observe
        render={() => {
          const isPosted = scheduleModel.getColumnIsPostedByPosition(cell.columnPosition);
          return (
            <div className={classes.root}>
              <span>{cell.value}</span>
              {isPosted ? (
                <PostedIcon className={classNames(classes.postedStatusIcon, classes.postedIcon)} />
              ) : (
                <UnpostedIcon className={classNames(classes.postedStatusIcon, classes.unpostedIcon)} />
              )}
            </div>
          );
        }}
      />
    );
  }),
  () => false,
);

const shiftEmployeeSource = {
  beginDrag(props: any, _monitor: any, _component: any) {
    const item = { timeEntries: props.timeEntries, timeEntry: props.timeEntry };
    return item;
  },
  endDrag(props: any, monitor: any, _component: any) {
    if (!monitor.didDrop()) {
      return;
    }

    const dropResult = monitor.getDropResult();
    const { dropEffect, sourceTimeEntries, sourceTimeEntry, targetTimeEntries, targetTimeEntry } = dropResult;
    const flattenedSourceTimeEntries = sourceTimeEntries.flat();
    const flattenedTargetTimeEntries = targetTimeEntries.flat();

    if (targetTimeEntry.employee) {
      confirmationModel.load(
        `An employee has already been scheduled for this shift. Do you want to replace the existing employee?`,
        {
          yesAction: () => {
            scheduleModel.dragAndDropEmployee(
              dropEffect,
              flattenedSourceTimeEntries,
              sourceTimeEntry,
              flattenedTargetTimeEntries,
              targetTimeEntry,
            );
            return true;
          },
          maxWidth: 'sm',
          title: 'Confirm Employee Move and Replace',
        },
      );
    } else {
      scheduleModel.dragAndDropEmployee(
        dropEffect,
        flattenedSourceTimeEntries,
        sourceTimeEntry,
        flattenedTargetTimeEntries,
        targetTimeEntry,
      );
    }
  },
};

function sourceCollect(connect: any, monitor: any) {
  return {
    connectDragSource: connect.dragSource(),
    isDragging: monitor.isDragging(),
  };
}

const shiftEmployeeTarget = {
  drop(props: any, monitor: any, _component: any) {
    const sourceItem = monitor.getItem();
    const sourceTimeEntries = sourceItem.timeEntries;
    const sourceTimeEntry = sourceItem.timeEntry;
    const targetTimeEntries = props.timeEntries;
    const targetTimeEntry = props.timeEntry;
    return {
      sourceTimeEntries,
      sourceTimeEntry,
      targetTimeEntries,
      targetTimeEntry,
    };
  },

  canDrop(props: any, monitor: any) {
    const sourceItem = monitor.getItem();
    const sourceEmployee = sourceItem.timeEntry.employee;
    const targetEmployee = props.timeEntry.employee;
    return sourceEmployee && targetEmployee ? sourceEmployee.id !== targetEmployee.id : sourceEmployee || targetEmployee;
  },
};

function targetCollect(connect: any, monitor: any) {
  return {
    connectDropTarget: connect.dropTarget(),
    highlighted: monitor.canDrop(),
    hovered: monitor.canDrop() && monitor.isOver(),
  };
}

const shiftCellEmployeeStyles = (theme: Theme) => ({
  employee: {
    lineHeight: '1em',
    padding: '4px 5px',
    border: '1px solid transparent',
    borderRadius: '10px',
    fontWeight: 500,
    '&:focus': {
      outline: 0,
    },
  },
  employeeHover: {
    cursor: 'grab',
    '&:hover': {
      background: theme.palette.dialogPrimary.main,
      color: theme.palette.dialogPrimary.contrastText,
      borderColor: theme.palette.dialogPrimary.main,
      overflow: 'hidden',
    },
  },
  timeEntryHasAbsenceReason: {
    backgroundColor: theme.palette.gridSelect.main,
    color: theme.palette.gridSelect.contrastText,
    overflow: 'hidden',
  },
  isDragging: {
    background: theme.palette.dialogPrimary.main,
    color: theme.palette.dialogPrimary.contrastText,
  },
  highlighted: {
    overflow: 'hidden',
  },
  highlightedEmployee: {
    borderColor: theme.palette.dialogPrimary.main,
  },
  highlightedNoEmployee: {
    borderColor: theme.palette.dialogPrimary.main,
  },
  hovered: {
    background: theme.palette.secondary.main,
    color: theme.palette.common.white,
    borderColor: theme.palette.secondary.main,
  },
});

const ShiftCellEmployee = flow([
  DragSource('shiftCellEmployee', shiftEmployeeSource, sourceCollect),
  DropTarget('shiftCellEmployee', shiftEmployeeTarget, targetCollect),
])(
  React.memo(
    withStyles(shiftCellEmployeeStyles, (props: any) => {
      const {
        classes,
        timeEntry,
        isGridMouseCellSelecting,
        isDragging,
        highlighted,
        hovered,
        connectDragSource,
        connectDropTarget,
      } = props;
      const employeeClasses = [classes.employee];
      if (!isGridMouseCellSelecting) {
        employeeClasses.push(classes.employeeHover);
      }
      if (isDragging) {
        employeeClasses.push(classes.isDragging);
      } else {
        if (highlighted) {
          employeeClasses.push(classes.highlighted);
          timeEntry.text.employee
            ? employeeClasses.push(classes.highlightedEmployee)
            : employeeClasses.push(classes.highlightedNoEmployee);
        }
        hovered && employeeClasses.push(classes.hovered);
      }
      const hasAbsenceReason = timeEntry.reason?.category === 'absence';
      if (hasAbsenceReason) {
        employeeClasses.push(classes.timeEntryHasAbsenceReason);
      }

      return connectDropTarget(
        connectDragSource(
          // biome-ignore lint/a11y/noStaticElementInteractions: <explanation>
          <div
            tabIndex={-1}
            className={classNames(...employeeClasses)}
            onFocus={(evt: React.FocusEvent<any>) => {
              evt.stopPropagation();
            }}
            onMouseDown={(evt: React.MouseEvent<any>) => evt.stopPropagation()}
          >
            <span>{timeEntry.text.employee || '------------'}</span>
          </div>,
        ),
      );
    }),
    () => false,
  ),
);

const shiftCellEmployeeViewOnlyStyles = (theme: Theme) => ({
  employee: {
    lineHeight: '1em',
    padding: '4px 5px',
    border: '1px solid transparent',
    borderRadius: '10px',
    fontWeight: 500,
    '&:focus': {
      outline: 0,
    },
  },
  timeEntryHasAbsenceReason: {
    backgroundColor: theme.palette.gridSelect.main,
    color: theme.palette.gridSelect.contrastText,
    overflow: 'hidden',
  },
  requestStatus: {
    backgroundColor: 'red',
    color: theme.palette.gridSelect.contrastText,
    overflow: 'hidden',
  },
});

const ShiftCellEmployeeViewOnly = React.memo(
  withStyles(shiftCellEmployeeViewOnlyStyles, (props: any) => {
    const { classes, timeEntry } = props;
    const employeeClasses = [classes.employee];

    const hasAbsenceReason = timeEntry.reason?.category === 'absence';
    if (hasAbsenceReason) {
      employeeClasses.push(classes.timeEntryHasAbsenceReason);
    }
    return (
      <div className={classNames(...employeeClasses)}>
        <span>{timeEntry.text.employee || '------------'}</span>
      </div>
    );
  }),
  () => false,
);

const shiftCellStyles = (theme: Theme) => ({
  root: {
    lineHeight: '1.5em',
    height: '100%',
    display: 'flex',
  },
  innerContainer: {
    lineHeight: '1.4em',
  },
  timeEntryContainer: {},
  employeeContainer: {
    lineHeight: '1.25em',
  },
  occupation: {
    marginBottom: '4px',
    fontWeight: '700',
    color: theme.palette.neutralLight.dark,
    fontSize: '1.15em',
  },
  requested: {
    marginBottom: '4px',
    fontWeight: '700',
    color: '#dd9719',
    textTransform: 'capitalize',
    fontSize: '1.15em',
  },
  approved: {
    marginBottom: '4px',
    fontWeight: '700',
    color: '#1976d2',
    textTransform: 'capitalize',
    fontSize: '1.15em',
  },
  cancelled: {
    marginBottom: '4px',
    fontWeight: '700',
    color: '#796d6c',
    textTransform: 'capitalize',
    fontSize: '1.15em',
  },
  declined: {
    marginBottom: '4px',
    fontWeight: '700',
    color: '#cc0000',
    textTransform: 'capitalize',
    fontSize: '1.15em',
  },
  template: {
    marginBottom: '4px',
    fontWeight: '700',
    color: theme.palette.neutralLight.dark,
    fontSize: '1.15em',
  },
  divider: {
    margin: '4px 0px',
    backgroundColor: theme.palette.neutralLight.dark,
  },
  shiftDivider: {
    height: '2px',
    margin: '6px 0px',
    backgroundColor: theme.palette.primary.main,
  },
  messageIconsContainer: {},
  messageIcon: {
    display: 'block',
    marginBottom: '1px',
  },
  messageIconIcon: {
    fontSize: '1.55em',
  },
  shiftEntries: {
    width: '100%',
  },
});

export const ScheduleShiftCell: React.SFC<ICellProps> = React.memo(
  withStyles(shiftCellStyles, (props) => {
    const { cell, classes } = props;
    return (
      <Observe
        render={() => {
          const shiftEntries = cell.value?.shiftEntries ?? [];
          const allShiftsTimeEntries = cell.value?.allShiftsTimeEntries ?? [];
          const jobId = cell.value ? cell.value.jobId : scheduleModel.getJobCellFromCell(cell)?.value?.id;
          const employeeId = scheduleModel.getEmployeeCellFromCell(cell)?.value?.id;
          const earnedDate = cell.value ? cell.value.earnedDate : scheduleModel.getShiftCellEarnedDate(cell);

          const messages =
            scheduleModel.viewMode === 'Job'
              ? scheduleModel.messagesSubscriptionModel.messages.filter((m) => m.jobId === jobId && m.effective?.equals(earnedDate))
              : scheduleModel.messagesSubscriptionModel.messages.filter(
                  (m) => m.employee?.id === employeeId && m.effective?.equals(earnedDate),
                );
          const messageIcons = messages.length ? (
            <div className={classes.messageIconsContainer}>
              {messages.map((message, idx) => (
                <MessageIcon
                  key={`message-${idx}`}
                  level={message.level}
                  className={classes.messageIcon}
                  classes={{ icon: classes.messageIconIcon }}
                  tooltip={message.text}
                />
              ))}
            </div>
          ) : null;
          if (!shiftEntries.length) {
            return messageIcons;
          }
          return (
            <div className={classes.root} style={{ textAlign: cell.align }}>
              {messageIcons}
              <div className={classes.shiftEntries}>
                {shiftEntries.map((shiftEntry: IShiftEntry, shiftEntryIdx: number) => {
                  const template = shiftEntry.templateText;
                  return (
                    <div key={`shift-entry-${shiftEntryIdx}`}>
                      {shiftEntryIdx !== 0 && <Divider className={classes.shiftDivider} />}
                      <div>
                        {template && <div className={classes.template}>{template}</div>}
                        {shiftEntry.timeEntries.map((timeEntry: ITimeEntry, timeEntryIdx: number) => {
                          return (
                            <div key={`time-entry-${timeEntryIdx}`}>
                              {timeEntryIdx !== 0 && <Divider className={classes.divider} />}
                              <div>
                                {scheduleModel.viewMode === 'Job' && (
                                  <div className={classes.employeeContainer}>
                                    {!scheduleModel.viewOnly ? (
                                      <ShiftCellEmployee
                                        timeEntries={allShiftsTimeEntries}
                                        timeEntry={timeEntry}
                                        isGridMouseCellSelecting={cell.grid.isMouseDown}
                                      />
                                    ) : (
                                      <ShiftCellEmployeeViewOnly timeEntry={timeEntry} />
                                    )}
                                  </div>
                                )}
                                {timeEntry.text.template && <div className={classes.template}>{timeEntry.text.template}</div>}
                                {timeEntry.text.occupation && <div className={classes.occupation}>{timeEntry.text.occupation}</div>}
                                {timeEntry.timeOffStatus && (
                                  <div className={classes[timeEntry?.timeOffStatus ?? '']}>{timeEntry.timeOffStatus}</div>
                                )}
                                <div className={classes.timeEntryContainer}>
                                  {cell.value.timeEntryTextToDetails(timeEntry.text)}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        }}
      />
    );
  }),
  () => false,
);
